/**
 * StatusEffectSystem — global registry and factory for status effect tracking.
 *
 * Loads status effect definitions from JSON configuration. Each definition
 * describes how an effect behaves: its category (DoT/CC/debuff/buff),
 * stacking rules, default values, and CC immunity windows.
 *
 * Creates per-entity StatusEffectTracker instances that manage the runtime
 * lifecycle of active effects — application, stacking, ticking, expiry,
 * and CC hierarchy enforcement.
 *
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import { StatusEffectTracker } from './StatusEffectTracker';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StatusCategory = 'dot' | 'cc' | 'debuff' | 'buff';
export type StackingRule = 'refresh' | 'intensity' | 'duration' | 'independent' | 'override';

/** Defines how a status effect behaves — loaded from JSON config. */
export interface StatusEffectDef {
  readonly id: string;
  readonly category: StatusCategory;
  readonly stackingRule: StackingRule;
  readonly maxStacks?: number;
  readonly defaultDurationMs?: number;  // -1 = permanent
  readonly defaultTickIntervalMs?: number;
  readonly defaultIntensity?: number;
  readonly immunityAfterExpiryMs?: number;
  readonly speedMultiplier?: number;    // movement speed factor (0–1), lower = slower
}

/** Raw JSON shape before adding the `id` field. */
interface StatusEffectDefJson {
  category: StatusCategory;
  stackingRule: StackingRule;
  maxStacks?: number;
  defaultDurationMs?: number;
  defaultTickIntervalMs?: number;
  defaultIntensity?: number;
  immunityAfterExpiryMs?: number;
  speedMultiplier?: number;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class StatusEffectSystem {
  private readonly defs = new Map<string, StatusEffectDef>();

  /** Register a status effect definition. */
  registerDef(def: StatusEffectDef): void {
    this.defs.set(def.id, def);
  }

  /** Bulk-register definitions from the JSON config shape ({ id: {...} }). */
  registerFromJson(json: Record<string, StatusEffectDefJson>): void {
    for (const [id, raw] of Object.entries(json)) {
      this.registerDef({ id, ...raw });
    }
  }

  /** Get a registered definition by ID. Returns undefined if not registered. */
  getDef(defId: string): StatusEffectDef | undefined {
    return this.defs.get(defId);
  }

  /** Create a new per-entity tracker wired to this system's definitions. */
  createTracker(): StatusEffectTracker {
    return new StatusEffectTracker(this);
  }
}
